import React from "react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { Brand } from "../components/Brand";
import { Loader } from "../components/Loader";
import { useAuthHeaders } from "../lib/auth";
import { deviceLink } from "../lib/api";
import { Button, Card, EmptyState, Page } from "../design";

type State = "loading" | "confirm" | "done" | "error";

// The onboarding wizard (same origin) records which harness the user picked;
// read it back so the done screen can name it. Absent → generic copy.
const HARNESS_NAMES: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
  opencode: "opencode",
};
function setupTarget(): string | null {
  try {
    const raw = localStorage.getItem("condense.onboarding.v1");
    if (!raw) return null;
    const id = (JSON.parse(raw) as { harnessId?: string }).harnessId;
    return id ? (HARNESS_NAMES[id] ?? null) : null;
  } catch {
    return null;
  }
}

export function Cli() {
  const { isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const authHeaders = useAuthHeaders();
  const params = new URLSearchParams(window.location.search);
  const userCode = params.get("code") || "";
  const [state, setState] = React.useState<State>(
    userCode ? "loading" : "error",
  );
  const [message, setMessage] = React.useState(
    userCode ? "" : "Missing ?code= in URL.",
  );

  React.useEffect(() => {
    if (!isLoaded || !userCode) return;
    if (!isSignedIn) {
      void clerk.redirectToSignIn({
        signInForceRedirectUrl: window.location.href,
        signUpForceRedirectUrl: window.location.href,
      });
      return;
    }
    setState("confirm");
  }, [clerk, isLoaded, isSignedIn, userCode]);

  async function approve() {
    setState("loading");
    const r = await deviceLink(authHeaders, userCode);
    if (r.ok) {
      setState("done");
    } else {
      setMessage(
        r.status === 404
          ? "That code is expired or already used."
          : `Server error ${r.status}`,
      );
      setState("error");
    }
  }

  const target = setupTarget();

  return (
    <div className="app-shell">
      <header className="app-header">
        <Brand />
      </header>
      <main>
        <div className="content-pad per-cli">
          <Page title="Link this terminal">
            {state === "loading" && <Loader />}
            {state === "confirm" && (
              <Card
                title="Confirm the code"
                info="Approving mints a 60-day API key for dense; you can revoke it anytime under API keys."
              >
                <p className="per-muted">
                  A copy of <code className="mono">dense</code> on your machine
                  is asking to authorize itself. Approve only if this code
                  matches the one your terminal printed.
                </p>
                <pre className="per-code mono">{userCode}</pre>
                <Button variant="primary" icon="check" onClick={approve}>
                  Approve
                </Button>
              </Card>
            )}
            {state === "done" && (
              <EmptyState icon="check" title="Denser already">
                You&rsquo;re all set up{target ? ` for ${target}` : ""}. You can
                now close this window.
              </EmptyState>
            )}
            {state === "error" && (
              <EmptyState icon="alert" title="Couldn't authorize">
                {message}
              </EmptyState>
            )}
          </Page>
        </div>
      </main>
    </div>
  );
}
